/**
 * 発注一覧フィルタパネル
 *
 * 設計書 05-frontend-design.md §5.1 整合。
 * ステータスタブ + 材料種別 / 着荷予定日範囲を `PurchaseOrderListQuery` の更新コールバックで親に通知。
 */
import { useMemo } from 'react';
import { DatePicker, Select, Space, Tabs, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
  PURCHASE_ORDER_STATUS,
  PURCHASE_ORDER_STATUS_DISPLAY_NAME,
} from '../types/purchaseOrder';
import type {
  ApplyFilterInput,
  MaterialTypeOption,
  PurchaseOrderListQuery,
  SiteFilterMode,
} from '../types/purchaseOrder';
import type { User } from '@/features/auth/types/auth.types';
import { isDev } from '@/features/auth/utils/permissionUtils';

const { RangePicker } = DatePicker;

const ISO_DATE_FORMAT = 'YYYY-MM-DD';
const SELECT_WIDTH_PX = 180;

/**
 * ステータスタブのキー
 *
 * `ORDERED/ARRIVED/CANCELLED` はドメイン定数 `PURCHASE_ORDER_STATUS` を参照し同期保証。
 * `ARCHIVED`/`ALL` は UI 専用キー(クエリの `status` ではなく `includeArchived` 等で表現)。
 */
const STATUS_TAB_KEY = {
  ORDERED: PURCHASE_ORDER_STATUS.ORDERED,
  ARRIVED: PURCHASE_ORDER_STATUS.ARRIVED,
  CANCELLED: PURCHASE_ORDER_STATUS.CANCELLED,
  ARCHIVED: 'ARCHIVED',
  ALL: 'ALL',
} as const;

type StatusTabKey = (typeof STATUS_TAB_KEY)[keyof typeof STATUS_TAB_KEY];

/**
 * 任意の文字列が `StatusTabKey` であるかの型ガード
 *
 * antd `Tabs.onChange` は `string` を渡してくるため、未知のキーが来た場合の
 * フォールバック判定(下記 `handleTabChange` で `ALL` 扱い)を可能にする。
 */
function isStatusTabKey(value: string): value is StatusTabKey {
  return (Object.values(STATUS_TAB_KEY) as readonly string[]).includes(value);
}

interface Props {
  readonly currentFilter: PurchaseOrderListQuery;
  readonly materialTypeOptions: readonly MaterialTypeOption[];
  /**
   * 現場フィルタの表示モード(判別共用体)
   *
   * 設計書 05-frontend-design.md §5.1「現場フィルタ(MCM/DEV: 選択可、SITE_*: 固定表示)」に整合。
   * `kind` で網羅性チェック付き分岐を行い、null + null の 2-prop 多義設計を回避する。
   */
  readonly siteFilterMode: SiteFilterMode;
  /**
   * 認証ユーザー(アーカイブ・キャンセル状態タブの表示制御に使用)
   *
   * v1.5: アーカイブ/キャンセル一覧は DEV_USER のみ閲覧可。サーバー側でも
   * 強制除外されるが、UI 動線でも非表示にすることで誤操作を防止する。
   */
  readonly user: User | null;
  readonly onFilterChange: (params: { readonly filter: ApplyFilterInput }) => void;
}

/**
 * 現在のフィルタからアクティブなタブキーを推定
 *
 * `includeArchived === true` && `status` 未指定 → ARCHIVED 扱い、
 * `status` 指定 → 該当タブ、未指定 → ALL。
 * `canViewArchive=false` のとき ARCHIVED/CANCELLED は描画されないタブのため ALL にフォールバックする
 * (将来 URL クエリ復元等が入っても無選択状態にならない防御)。
 */
function deriveActiveTabKey(params: {
  readonly filter: PurchaseOrderListQuery;
  readonly canViewArchive: boolean;
}): StatusTabKey {
  const { filter, canViewArchive } = params;
  if (filter.includeArchived === true) {
    return canViewArchive ? STATUS_TAB_KEY.ARCHIVED : STATUS_TAB_KEY.ALL;
  }
  switch (filter.status) {
    case 'ORDERED':
      return STATUS_TAB_KEY.ORDERED;
    case 'ARRIVED':
      return STATUS_TAB_KEY.ARRIVED;
    case 'CANCELLED':
      return canViewArchive ? STATUS_TAB_KEY.CANCELLED : STATUS_TAB_KEY.ALL;
    default:
      return STATUS_TAB_KEY.ALL;
  }
}

/**
 * タブキー変更時の filter 差分計算
 */
function buildFilterForTab(params: {
  readonly tabKey: StatusTabKey;
  readonly currentFilter: PurchaseOrderListQuery;
}): ApplyFilterInput {
  const { tabKey, currentFilter } = params;
  // ステータス系プロパティをリセットして再構築(page はフック側で FIRST_PAGE にリセットされる)
  const baseFilter: ApplyFilterInput = {
    siteId: currentFilter.siteId,
    materialTypeId: currentFilter.materialTypeId,
    expectedArrivalDateFrom: currentFilter.expectedArrivalDateFrom,
    expectedArrivalDateTo: currentFilter.expectedArrivalDateTo,
    limit: currentFilter.limit,
  };
  switch (tabKey) {
    case STATUS_TAB_KEY.ORDERED:
      return { ...baseFilter, status: 'ORDERED' };
    case STATUS_TAB_KEY.ARRIVED:
      return { ...baseFilter, status: 'ARRIVED' };
    case STATUS_TAB_KEY.CANCELLED:
      return { ...baseFilter, status: 'CANCELLED' };
    case STATUS_TAB_KEY.ARCHIVED:
      return { ...baseFilter, includeArchived: true };
    case STATUS_TAB_KEY.ALL:
      return baseFilter;
  }
}

export function PurchaseOrderFilterPanel({
  currentFilter,
  materialTypeOptions,
  siteFilterMode,
  user,
  onFilterChange,
}: Props): React.ReactElement {
  const canViewArchive = isDev(user);
  const activeTabKey = useMemo(
    () => deriveActiveTabKey({ filter: currentFilter, canViewArchive }),
    [currentFilter, canViewArchive]
  );
  const tabItems: readonly { readonly key: StatusTabKey; readonly label: string }[] = [
    { key: STATUS_TAB_KEY.ORDERED, label: PURCHASE_ORDER_STATUS_DISPLAY_NAME.ORDERED },
    { key: STATUS_TAB_KEY.ARRIVED, label: PURCHASE_ORDER_STATUS_DISPLAY_NAME.ARRIVED },
    ...(canViewArchive
      ? ([
          { key: STATUS_TAB_KEY.CANCELLED, label: PURCHASE_ORDER_STATUS_DISPLAY_NAME.CANCELLED },
          { key: STATUS_TAB_KEY.ARCHIVED, label: 'アーカイブ' },
        ] as const)
      : []),
    { key: STATUS_TAB_KEY.ALL, label: '全て' },
  ];

  const handleTabChange = (nextTabKey: string): void => {
    const resolvedTabKey: StatusTabKey = isStatusTabKey(nextTabKey)
      ? nextTabKey
      : STATUS_TAB_KEY.ALL;
    onFilterChange({
      filter: buildFilterForTab({
        tabKey: resolvedTabKey,
        currentFilter,
      }),
    });
  };

  const handleMaterialTypeChange = (value: number | undefined): void => {
    onFilterChange({
      filter: { ...currentFilter, materialTypeId: value },
    });
  };

  const handleSiteChange = (value: number | undefined): void => {
    onFilterChange({
      filter: { ...currentFilter, siteId: value },
    });
  };

  const handleDateRangeChange = (
    range: readonly [Dayjs | null, Dayjs | null] | null
  ): void => {
    if (range === null) {
      onFilterChange({
        filter: {
          ...currentFilter,
          expectedArrivalDateFrom: undefined,
          expectedArrivalDateTo: undefined,
        },
      });
      return;
    }
    const [from, to] = range;
    onFilterChange({
      filter: {
        ...currentFilter,
        expectedArrivalDateFrom: from?.format(ISO_DATE_FORMAT),
        expectedArrivalDateTo: to?.format(ISO_DATE_FORMAT),
      },
    });
  };

  const dateRangeValue: [Dayjs | null, Dayjs | null] = [
    currentFilter.expectedArrivalDateFrom !== undefined
      ? dayjs(currentFilter.expectedArrivalDateFrom)
      : null,
    currentFilter.expectedArrivalDateTo !== undefined
      ? dayjs(currentFilter.expectedArrivalDateTo)
      : null,
  ];

  return (
    <>
      <Tabs
        activeKey={activeTabKey}
        onChange={handleTabChange}
        items={tabItems.map((tab) => ({ key: tab.key, label: tab.label }))}
      />
      <Space size="middle" wrap>
        {siteFilterMode.kind === 'selectable' && (
          <Select
            allowClear
            placeholder="現場"
            options={[...siteFilterMode.options]}
            value={currentFilter.siteId}
            onChange={handleSiteChange}
            style={{ width: SELECT_WIDTH_PX }}
          />
        )}
        {siteFilterMode.kind === 'fixed' && (
          <Typography.Text type="secondary" className="text-sm">
            現場: {siteFilterMode.label}
          </Typography.Text>
        )}
        <Select
          allowClear
          placeholder="材料種別"
          options={[...materialTypeOptions]}
          value={currentFilter.materialTypeId}
          onChange={handleMaterialTypeChange}
          style={{ width: SELECT_WIDTH_PX }}
        />
        <RangePicker
          value={dateRangeValue}
          onChange={handleDateRangeChange}
          format={ISO_DATE_FORMAT}
          placeholder={['入庫予定日 開始', '入庫予定日 終了']}
        />
        {/* TODO(後続PR): 発注者フィルタ(MCM/DEV のみ表示)を追加する。
            現在 faro-api の `PurchaseOrderListQueryDTO` に `orderedByUserId` フィールドが
            未定義のため、サーバー側 DTO 拡張・Service フィルタ実装と一体で対応する必要がある。
            設計書 05-frontend-design.md §5.1 図参照。 */}
      </Space>
    </>
  );
}
